/**
 * Comprehensive Countries and Cities Data
 *
 * This file provides complete country and city data for restaurant profile setup.
 * Includes ISO country codes, currency information, and curated
 * Región/Provincia/Estado (admin-1) suggestions for each country.
 */

// ============================================================================
// Types
// ============================================================================

export interface Country {
  code: string; // ISO 3166-1 alpha-2 code
  name: string; // Country name in Spanish
  nameEn: string; // Country name in English
  currency: string; // ISO 4217 currency code
  cities: string[]; // Curated Región/Provincia/Estado suggestions (admin-1)
  flag?: string; // Unicode flag emoji
}

export interface CountryOption {
  value: string; // Country code
  label: string; // Country name for display
  currency: string; // Currency code
  flag: string | undefined; // Flag emoji
}

export interface CityOption {
  value: string; // Región/Provincia/Estado name
  label: string; // Display label
  countryCode: string; // Parent country code
}

// ============================================================================
// Countries Data
// ============================================================================

export const COUNTRIES: Country[] = [
  // Europe
  {
    code: 'ES',
    name: 'España',
    nameEn: 'Spain',
    currency: 'EUR',
    flag: '🇪🇸',
    cities: [
      'A Coruña',
      'Álava',
      'Albacete',
      'Alicante',
      'Almería',
      'Asturias',
      'Ávila',
      'Badajoz',
      'Barcelona',
      'Burgos',
      'Cáceres',
      'Cádiz',
      'Cantabria',
      'Castellón',
      'Ceuta',
      'Ciudad Real',
      'Córdoba',
      'Cuenca',
      'Girona',
      'Granada',
      'Guadalajara',
      'Guipúzcoa',
      'Huelva',
      'Huesca',
      'Islas Baleares',
      'Jaén',
      'La Rioja',
      'Las Palmas',
      'León',
      'Lleida',
      'Lugo',
      'Madrid',
      'Málaga',
      'Melilla',
      'Murcia',
      'Navarra',
      'Ourense',
      'Palencia',
      'Pontevedra',
      'Salamanca',
      'Santa Cruz de Tenerife',
      'Segovia',
      'Sevilla',
      'Soria',
      'Tarragona',
      'Teruel',
      'Toledo',
      'Valencia',
      'Valladolid',
      'Vizcaya',
      'Zamora',
      'Zaragoza',
    ],
  },
  {
    code: 'FR',
    name: 'Francia',
    nameEn: 'France',
    currency: 'EUR',
    flag: '🇫🇷',
    cities: [
      'Alta Francia',
      'Auvernia-Ródano-Alpes',
      'Borgoña-Franco Condado',
      'Bretaña',
      'Centro-Valle de Loira',
      'Córcega',
      'Gran Este',
      'Isla de Francia',
      'Normandía',
      'Nueva Aquitania',
      'Occitania',
      'Países del Loira',
      'Provenza-Alpes-Costa Azul',
    ],
  },
  {
    code: 'IT',
    name: 'Italia',
    nameEn: 'Italy',
    currency: 'EUR',
    flag: '🇮🇹',
    cities: [
      'Abruzos',
      'Apulia',
      'Basilicata',
      'Calabria',
      'Campania',
      'Cerdeña',
      'Emilia-Romaña',
      'Friuli-Venecia Julia',
      'Lacio',
      'Liguria',
      'Lombardía',
      'Marcas',
      'Molise',
      'Piamonte',
      'Sicilia',
      'Toscana',
      'Trentino-Alto Adigio',
      'Umbría',
      'Valle de Aosta',
      'Véneto',
    ],
  },
  {
    code: 'DE',
    name: 'Alemania',
    nameEn: 'Germany',
    currency: 'EUR',
    flag: '🇩🇪',
    cities: [
      'Baden-Württemberg',
      'Baviera',
      'Berlín',
      'Brandeburgo',
      'Bremen',
      'Hamburgo',
      'Hesse',
      'Mecklemburgo-Pomerania Occidental',
      'Baja Sajonia',
      'Renania del Norte-Westfalia',
      'Renania-Palatinado',
      'Sarre',
      'Sajonia',
      'Sajonia-Anhalt',
      'Schleswig-Holstein',
      'Turingia',
    ],
  },
  {
    code: 'GB',
    name: 'Reino Unido',
    nameEn: 'United Kingdom',
    currency: 'GBP',
    flag: '🇬🇧',
    cities: ['Inglaterra', 'Escocia', 'Gales', 'Irlanda del Norte'],
  },
  {
    code: 'PT',
    name: 'Portugal',
    nameEn: 'Portugal',
    currency: 'EUR',
    flag: '🇵🇹',
    cities: [
      'Aveiro',
      'Azores',
      'Beja',
      'Braga',
      'Braganza',
      'Castelo Branco',
      'Coímbra',
      'Évora',
      'Faro',
      'Guarda',
      'Leiria',
      'Lisboa',
      'Madeira',
      'Oporto',
      'Portalegre',
      'Santarém',
      'Setúbal',
      'Viana do Castelo',
      'Vila Real',
      'Viseu',
    ],
  },
  {
    code: 'NL',
    name: 'Países Bajos',
    nameEn: 'Netherlands',
    currency: 'EUR',
    flag: '🇳🇱',
    cities: [
      'Brabante Septentrional',
      'Drenthe',
      'Flevoland',
      'Frisia',
      'Groninga',
      'Güeldres',
      'Holanda Meridional',
      'Holanda Septentrional',
      'Limburgo',
      'Overijssel',
      'Utrecht',
      'Zelanda',
    ],
  },
  {
    code: 'BE',
    name: 'Bélgica',
    nameEn: 'Belgium',
    currency: 'EUR',
    flag: '🇧🇪',
    cities: [
      'Amberes',
      'Brabante Flamenco',
      'Brabante Valón',
      'Flandes Occidental',
      'Flandes Oriental',
      'Henao',
      'Lieja',
      'Limburgo',
      'Luxemburgo',
      'Namur',
      'Región de Bruselas-Capital',
    ],
  },
  {
    code: 'CH',
    name: 'Suiza',
    nameEn: 'Switzerland',
    currency: 'CHF',
    flag: '🇨🇭',
    cities: [
      'Argovia',
      'Appenzell Rodas Exteriores',
      'Appenzell Rodas Interiores',
      'Basilea-Campiña',
      'Basilea-Ciudad',
      'Berna',
      'Friburgo',
      'Ginebra',
      'Glaris',
      'Grisones',
      'Jura',
      'Lucerna',
      'Neuchâtel',
      'Nidwalden',
      'Obwalden',
      'San Galo',
      'Schaffhausen',
      'Schwyz',
      'Soleura',
      'Tesino',
      'Turgovia',
      'Uri',
      'Valais',
      'Vaud',
      'Zug',
      'Zúrich',
    ],
  },
  {
    code: 'AT',
    name: 'Austria',
    nameEn: 'Austria',
    currency: 'EUR',
    flag: '🇦🇹',
    cities: [
      'Alta Austria',
      'Baja Austria',
      'Burgenland',
      'Carintia',
      'Estiria',
      'Salzburgo',
      'Tirol',
      'Viena',
      'Vorarlberg',
    ],
  },

  // Americas
  {
    code: 'AR',
    name: 'Argentina',
    nameEn: 'Argentina',
    currency: 'ARS',
    flag: '🇦🇷',
    cities: [
      'Buenos Aires',
      'Catamarca',
      'Chaco',
      'Chubut',
      'Ciudad Autónoma de Buenos Aires',
      'Córdoba',
      'Corrientes',
      'Entre Ríos',
      'Formosa',
      'Jujuy',
      'La Pampa',
      'La Rioja',
      'Mendoza',
      'Misiones',
      'Neuquén',
      'Río Negro',
      'Salta',
      'San Juan',
      'San Luis',
      'Santa Cruz',
      'Santa Fe',
      'Santiago del Estero',
      'Tierra del Fuego',
      'Tucumán',
    ],
  },
  {
    code: 'BR',
    name: 'Brasil',
    nameEn: 'Brazil',
    currency: 'BRL',
    flag: '🇧🇷',
    cities: [
      'Acre',
      'Alagoas',
      'Amapá',
      'Amazonas',
      'Bahía',
      'Ceará',
      'Distrito Federal',
      'Espírito Santo',
      'Goiás',
      'Maranhão',
      'Mato Grosso',
      'Mato Grosso do Sul',
      'Minas Gerais',
      'Pará',
      'Paraíba',
      'Paraná',
      'Pernambuco',
      'Piauí',
      'Río de Janeiro',
      'Rio Grande do Norte',
      'Rio Grande do Sul',
      'Rondônia',
      'Roraima',
      'Santa Catarina',
      'São Paulo',
      'Sergipe',
      'Tocantins',
    ],
  },
  {
    code: 'MX',
    name: 'México',
    nameEn: 'Mexico',
    currency: 'MXN',
    flag: '🇲🇽',
    cities: [
      'Aguascalientes',
      'Baja California',
      'Baja California Sur',
      'Campeche',
      'Chiapas',
      'Chihuahua',
      'Ciudad de México',
      'Coahuila',
      'Colima',
      'Durango',
      'Estado de México',
      'Guanajuato',
      'Guerrero',
      'Hidalgo',
      'Jalisco',
      'Michoacán',
      'Morelos',
      'Nayarit',
      'Nuevo León',
      'Oaxaca',
      'Puebla',
      'Querétaro',
      'Quintana Roo',
      'San Luis Potosí',
      'Sinaloa',
      'Sonora',
      'Tabasco',
      'Tamaulipas',
      'Tlaxcala',
      'Veracruz',
      'Yucatán',
      'Zacatecas',
    ],
  },
  {
    code: 'US',
    name: 'Estados Unidos',
    nameEn: 'United States',
    currency: 'USD',
    flag: '🇺🇸',
    cities: [
      'Alabama',
      'Alaska',
      'Arizona',
      'Arkansas',
      'California',
      'Carolina del Norte',
      'Carolina del Sur',
      'Colorado',
      'Connecticut',
      'Dakota del Norte',
      'Dakota del Sur',
      'Delaware',
      'District of Columbia',
      'Florida',
      'Georgia',
      'Hawái',
      'Idaho',
      'Illinois',
      'Indiana',
      'Iowa',
      'Kansas',
      'Kentucky',
      'Luisiana',
      'Maine',
      'Maryland',
      'Massachusetts',
      'Míchigan',
      'Minnesota',
      'Misisipi',
      'Misuri',
      'Montana',
      'Nebraska',
      'Nevada',
      'Nueva Hampshire',
      'Nueva Jersey',
      'Nueva York',
      'Nuevo México',
      'Ohio',
      'Oklahoma',
      'Oregón',
      'Pensilvania',
      'Rhode Island',
      'Tennessee',
      'Texas',
      'Utah',
      'Vermont',
      'Virginia',
      'Virginia Occidental',
      'Washington',
      'Wisconsin',
      'Wyoming',
    ],
  },
  {
    code: 'CA',
    name: 'Canadá',
    nameEn: 'Canada',
    currency: 'CAD',
    flag: '🇨🇦',
    cities: [
      'Alberta',
      'Columbia Británica',
      'Isla del Príncipe Eduardo',
      'Manitoba',
      'Nuevo Brunswick',
      'Nueva Escocia',
      'Nunavut',
      'Ontario',
      'Quebec',
      'Saskatchewan',
      'Terranova y Labrador',
      'Territorios del Noroeste',
      'Yukón',
    ],
  },
  {
    code: 'CO',
    name: 'Colombia',
    nameEn: 'Colombia',
    currency: 'COP',
    flag: '🇨🇴',
    cities: [
      'Amazonas',
      'Antioquia',
      'Arauca',
      'Atlántico',
      'Bogotá D.C.',
      'Bolívar',
      'Boyacá',
      'Caldas',
      'Caquetá',
      'Casanare',
      'Cauca',
      'Cesar',
      'Chocó',
      'Córdoba',
      'Cundinamarca',
      'Guainía',
      'Guaviare',
      'Huila',
      'La Guajira',
      'Magdalena',
      'Meta',
      'Nariño',
      'Norte de Santander',
      'Putumayo',
      'Quindío',
      'Risaralda',
      'San Andrés y Providencia',
      'Santander',
      'Sucre',
      'Tolima',
      'Valle del Cauca',
      'Vaupés',
      'Vichada',
    ],
  },
  {
    code: 'CL',
    name: 'Chile',
    nameEn: 'Chile',
    currency: 'CLP',
    flag: '🇨🇱',
    cities: [
      'Antofagasta',
      'Arica y Parinacota',
      'Atacama',
      'Aysén',
      'Biobío',
      'Coquimbo',
      'La Araucanía',
      'Los Lagos',
      'Los Ríos',
      'Magallanes y de la Antártica Chilena',
      'Maule',
      'Metropolitana de Santiago',
      'Ñuble',
      "O'Higgins",
      'Tarapacá',
      'Valparaíso',
    ],
  },
  {
    code: 'PE',
    name: 'Perú',
    nameEn: 'Peru',
    currency: 'PEN',
    flag: '🇵🇪',
    cities: [
      'Amazonas',
      'Áncash',
      'Apurímac',
      'Arequipa',
      'Ayacucho',
      'Cajamarca',
      'Callao',
      'Cusco',
      'Huancavelica',
      'Huánuco',
      'Ica',
      'Junín',
      'La Libertad',
      'Lambayeque',
      'Lima',
      'Loreto',
      'Madre de Dios',
      'Moquegua',
      'Pasco',
      'Piura',
      'Puno',
      'San Martín',
      'Tacna',
      'Tumbes',
      'Ucayali',
    ],
  },

  // Asia
  {
    code: 'JP',
    name: 'Japón',
    nameEn: 'Japan',
    currency: 'JPY',
    flag: '🇯🇵',
    cities: [
      'Aichi',
      'Akita',
      'Aomori',
      'Chiba',
      'Ehime',
      'Fukui',
      'Fukuoka',
      'Fukushima',
      'Gifu',
      'Gunma',
      'Hiroshima',
      'Hokkaido',
      'Hyogo',
      'Ibaraki',
      'Ishikawa',
      'Iwate',
      'Kagawa',
      'Kagoshima',
      'Kanagawa',
      'Kochi',
      'Kumamoto',
      'Kioto',
      'Mie',
      'Miyagi',
      'Miyazaki',
      'Nagano',
      'Nagasaki',
      'Nara',
      'Niigata',
      'Oita',
      'Okayama',
      'Okinawa',
      'Osaka',
      'Saga',
      'Saitama',
      'Shiga',
      'Shimane',
      'Shizuoka',
      'Tochigi',
      'Tokio',
      'Tokushima',
      'Tottori',
      'Toyama',
      'Wakayama',
      'Yamagata',
      'Yamaguchi',
      'Yamanashi',
    ],
  },
  {
    code: 'CN',
    name: 'China',
    nameEn: 'China',
    currency: 'CNY',
    flag: '🇨🇳',
    cities: [
      'Anhui',
      'Beijing',
      'Chongqing',
      'Fujian',
      'Gansu',
      'Guangdong',
      'Guangxi',
      'Guizhou',
      'Hainan',
      'Hebei',
      'Heilongjiang',
      'Henan',
      'Hong Kong',
      'Hubei',
      'Hunan',
      'Jiangsu',
      'Jiangxi',
      'Jilin',
      'Liaoning',
      'Macao',
      'Mongolia Interior',
      'Ningxia',
      'Qinghai',
      'Shaanxi',
      'Shandong',
      'Shanghai',
      'Shanxi',
      'Sichuan',
      'Taiwán',
      'Tianjin',
      'Tíbet',
      'Xinjiang',
      'Yunnan',
      'Zhejiang',
    ],
  },
  {
    code: 'IN',
    name: 'India',
    nameEn: 'India',
    currency: 'INR',
    flag: '🇮🇳',
    cities: [
      'Andaman and Nicobar Islands',
      'Andhra Pradesh',
      'Arunachal Pradesh',
      'Assam',
      'Bihar',
      'Chandigarh',
      'Chhattisgarh',
      'Dadra and Nagar Haveli and Daman and Diu',
      'Delhi',
      'Goa',
      'Gujarat',
      'Haryana',
      'Himachal Pradesh',
      'Jammu and Kashmir',
      'Jharkhand',
      'Karnataka',
      'Kerala',
      'Ladakh',
      'Lakshadweep',
      'Madhya Pradesh',
      'Maharashtra',
      'Manipur',
      'Meghalaya',
      'Mizoram',
      'Nagaland',
      'Odisha',
      'Punjab',
      'Puducherry',
      'Rajasthan',
      'Sikkim',
      'Tamil Nadu',
      'Telangana',
      'Tripura',
      'Uttar Pradesh',
      'Uttarakhand',
      'West Bengal',
    ],
  },
  {
    code: 'KR',
    name: 'Corea del Sur',
    nameEn: 'South Korea',
    currency: 'KRW',
    flag: '🇰🇷',
    cities: [
      'Busan',
      'Chungcheongbuk-do',
      'Chungcheongnam-do',
      'Daegu',
      'Daejeon',
      'Gangwon-do',
      'Gwangju',
      'Gyeonggi-do',
      'Gyeongsangbuk-do',
      'Gyeongsangnam-do',
      'Incheon',
      'Jeju-do',
      'Jeollabuk-do',
      'Jeollanam-do',
      'Sejong',
      'Seúl',
      'Ulsan',
    ],
  },

  // Oceania
  {
    code: 'AU',
    name: 'Australia',
    nameEn: 'Australia',
    currency: 'AUD',
    flag: '🇦🇺',
    cities: [
      'Australia Meridional',
      'Australia Occidental',
      'Nueva Gales del Sur',
      'Queensland',
      'Tasmania',
      'Territorio de la Capital Australiana',
      'Territorio del Norte',
      'Victoria',
    ],
  },
  {
    code: 'NZ',
    name: 'Nueva Zelanda',
    nameEn: 'New Zealand',
    currency: 'NZD',
    flag: '🇳🇿',
    cities: [
      'Auckland',
      'Bay of Plenty',
      'Canterbury',
      'Gisborne',
      "Hawke's Bay",
      'Manawatu-Whanganui',
      'Marlborough',
      'Nelson',
      'Northland',
      'Otago',
      'Southland',
      'Taranaki',
      'Tasman',
      'Waikato',
      'Wellington',
      'West Coast',
    ],
  },

  // Africa
  {
    code: 'ZA',
    name: 'Sudáfrica',
    nameEn: 'South Africa',
    currency: 'ZAR',
    flag: '🇿🇦',
    cities: [
      'Cabo Occidental',
      'Cabo Oriental',
      'Cabo Septentrional',
      'Estado Libre',
      'Gauteng',
      'KwaZulu-Natal',
      'Limpopo',
      'Mpumalanga',
      'Noroeste',
    ],
  },
  {
    code: 'MA',
    name: 'Marruecos',
    nameEn: 'Morocco',
    currency: 'MAD',
    flag: '🇲🇦',
    cities: [
      'Beni Melal-Jenifra',
      'Casablanca-Settat',
      'Dajla-Oued Ed-Dahab',
      'Drâa-Tafilalet',
      'Fez-Mequinez',
      'Guelmim-Oued Noun',
      'Laayoune-Sakia El Hamra',
      'Marrakech-Safi',
      'Oriental',
      'Rabat-Salé-Kenitra',
      'Souss-Massa',
      'Tánger-Tetuán-Alhucemas',
    ],
  },

  // Additional European countries
  {
    code: 'IE',
    name: 'Irlanda',
    nameEn: 'Ireland',
    currency: 'EUR',
    flag: '🇮🇪',
    cities: ['Connacht', 'Leinster', 'Munster', 'Ulster'],
  },
  {
    code: 'NO',
    name: 'Noruega',
    nameEn: 'Norway',
    currency: 'NOK',
    flag: '🇳🇴',
    cities: [
      'Agder',
      'Innlandet',
      'Møre og Romsdal',
      'Nordland',
      'Oslo',
      'Rogaland',
      'Troms og Finnmark',
      'Trøndelag',
      'Vestfold og Telemark',
      'Vestland',
      'Viken',
    ],
  },
  {
    code: 'SE',
    name: 'Suecia',
    nameEn: 'Sweden',
    currency: 'SEK',
    flag: '🇸🇪',
    cities: [
      'Blekinge',
      'Dalarna',
      'Estocolmo',
      'Gävleborg',
      'Gotland',
      'Halland',
      'Jämtland',
      'Jönköping',
      'Kalmar',
      'Kronoberg',
      'Norrbotten',
      'Örebro',
      'Östergötland',
      'Skåne',
      'Södermanland',
      'Uppsala',
      'Värmland',
      'Västerbotten',
      'Västernorrland',
      'Västmanland',
      'Västra Götaland',
    ],
  },
  {
    code: 'DK',
    name: 'Dinamarca',
    nameEn: 'Denmark',
    currency: 'DKK',
    flag: '🇩🇰',
    cities: [
      'Región Capital',
      'Región Central',
      'Región de Dinamarca Meridional',
      'Región de Nordjylland',
      'Región de Selandia',
    ],
  },
  {
    code: 'FI',
    name: 'Finlandia',
    nameEn: 'Finland',
    currency: 'EUR',
    flag: '🇫🇮',
    cities: [
      'Åland Islands',
      'Central Finland',
      'Central Ostrobothnia',
      'Kainuu',
      'Kymenlaakso',
      'Lapland',
      'North Karelia',
      'North Savo',
      'Northern Ostrobothnia',
      'Ostrobothnia',
      'Päijät-Häme',
      'Pirkanmaa',
      'Satakunta',
      'South Karelia',
      'South Ostrobothnia',
      'South Savo',
      'Southwest Finland',
      'Tavastia Proper',
      'Uusimaa',
    ],
  },
  {
    code: 'PL',
    name: 'Polonia',
    nameEn: 'Poland',
    currency: 'PLN',
    flag: '🇵🇱',
    cities: [
      'Baja Silesia',
      'Cuyavia-Pomerania',
      'Gran Polonia',
      'Lublin',
      'Lubusz',
      'Łódź',
      'Mazovia',
      'Opole',
      'Pequeña Polonia',
      'Podlaquia',
      'Pomerania',
      'Pomerania Occidental',
      'Silesia',
      'Subcarpatia',
      'Santa Cruz',
      'Varmia-Masuria',
    ],
  },
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get all countries as select options
 */
export function getCountryOptions(): CountryOption[] {
  return COUNTRIES.map((country) => ({
    value: country.code,
    label: country.name,
    currency: country.currency,
    flag: country.flag,
  })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * Get cities for a specific country
 */
export function getCitiesForCountry(countryCode: string): CityOption[] {
  const country = COUNTRIES.find((c) => c.code === countryCode);
  if (!country) return [];

  return country.cities
    .map((city) => ({
      value: city,
      label: city,
      countryCode: country.code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * Get country by code
 */
export function getCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find((country) => country.code === code);
}

/**
 * Get country by name (for backwards compatibility)
 */
export function getCountryByName(name: string): Country | undefined {
  return COUNTRIES.find(
    (country) =>
      country.name === name ||
      country.nameEn === name ||
      country.name.toLowerCase() === name.toLowerCase() ||
      country.nameEn.toLowerCase() === name.toLowerCase(),
  );
}

/**
 * Search countries by name
 */
export function searchCountries(query: string): CountryOption[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return getCountryOptions();

  return COUNTRIES.filter(
    (country) =>
      country.name.toLowerCase().includes(normalizedQuery) ||
      country.nameEn.toLowerCase().includes(normalizedQuery),
  )
    .map((country) => ({
      value: country.code,
      label: country.name,
      currency: country.currency,
      flag: country.flag,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * Search cities within a country
 */
export function searchCities(countryCode: string, query: string): CityOption[] {
  const cities = getCitiesForCountry(countryCode);
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return cities;

  return cities.filter((city) =>
    city.label.toLowerCase().includes(normalizedQuery),
  );
}

/**
 * Get currency for country code
 */
export function getCurrencyForCountryCode(countryCode: string): string {
  const country = getCountryByCode(countryCode);
  return country?.currency || 'EUR';
}

// ============================================================================
// Migration Helpers
// ============================================================================

/**
 * Map old country names to new country codes
 * For data migration from existing profiles
 */
export const LEGACY_COUNTRY_MAPPING: Record<string, string> = {
  España: 'ES',
  Francia: 'FR',
  Italia: 'IT',
  Portugal: 'PT',
  'Reino Unido': 'GB',
  Alemania: 'DE',
  'Países Bajos': 'NL',
  Bélgica: 'BE',
  Suiza: 'CH',
  Austria: 'AT',
  'Estados Unidos': 'US',
  México: 'MX',
  Canadá: 'CA',
  Argentina: 'AR',
  Brasil: 'BR',
  Colombia: 'CO',
  Chile: 'CL',
  Perú: 'PE',
  Japón: 'JP',
  Australia: 'AU',
  'Nueva Zelanda': 'NZ',
};

/**
 * Migrate legacy country name to country code
 */
export function migrateLegacyCountryName(legacyName: string): string {
  // If it's already a valid country code, return as-is
  const upperName = legacyName.toUpperCase();
  if (getCountryByCode(upperName)) {
    return upperName;
  }

  return (
    LEGACY_COUNTRY_MAPPING[legacyName] ||
    getCountryByName(legacyName)?.code ||
    'ES'
  ); // Default to Spain if not found
}
